/**
 * Retrieves a value from the browser's local storage by its key.
 *
 * This function attempts to fetch and parse the stored value in local storage as JSON.
 * If the value does not exist or cannot be parsed, it returns a provided default value.
 * Commonly used to persist and retrieve user-specific application data across sessions.
 *
 * @param key The key identifying the item in local storage to retrieve.
 * @param defaultValue The fallback value returned if the key does not exist or if parsing fails.
 * @return The parsed value from local storage if it exists and is valid JSON; otherwise, the provided default value.
 * @throws Will log an error to the console if JSON parsing fails.
 */
export function getLocalStorage( key: string, defaultValue: any ) {

    const value = localStorage.getItem( key );

    if( value !== null && typeof value !== 'undefined' ) {
        try {
            return JSON.parse( value );
        } catch( error: any ) {
            console.error( `Error parsing JSON for key "${key}": `, error );
            return defaultValue;

        }
    } else {
        return defaultValue;
    }
}

/**
 * Stores a key-value pair in the browser's local storage.
 *
 * This function is used to persist data in the browser that needs to be retained
 * even after the page is refreshed or the browser is closed. The value is serialized
 * into a JSON string before being stored.
 *
 * @param key - The unique identifier for the value to be stored. It must be a string.
 * @param value - The data to be stored. This can be of any type, but it will be converted to JSON format.
 * @return void - This method does not return a value.
 * @throws Error - Throws an error if the value cannot be serialized into JSON, or if local storage is unavailable.
 */
export function setLocalStorage( key: string, value: any ) {
    localStorage.setItem( key, JSON.stringify( value ) );
}